import { z } from 'zod'
import type { InvoiceOrigin, InvoiceStatus } from './werkbonnen'

// ─── FO-443 (batch-60 facturen-verwijderen) ──────────────────────────────────
// Verwijderen van onjuist toegevoegde (Finqle-)concepten en imports: de
// origin-bewuste verwijderbaarheid (resolver in @streep/utils), het
// verwijder-record, de genegeerd-marker en de request-/response-vormen.
// Gedeeld door de API (routes/invoices-verwijderen.ts, services/
// factuur-verwijderen.ts), de Finqle-aanmaakpaden en de backoffice-UI.

/** Waarom/hoe een factuur verwijderbaar is (spiegel van de Prisma-enum). */
export const FACTUUR_VERWIJDER_SOORTEN = [
  'EIGEN_CONCEPT',
  'EIGEN_MET_NUMMER',
  'IMPORT',
  'FINQLE_PROFORMA',
  'FINQLE_IMPORT',
  'FINQLE_SPIEGEL',
] as const
export type FactuurVerwijderSoort = (typeof FACTUUR_VERWIJDER_SOORTEN)[number]

export const FACTUUR_VERWIJDER_SOORT_LABELS: Record<FactuurVerwijderSoort, string> = {
  EIGEN_CONCEPT: 'Eigen concept',
  EIGEN_MET_NUMMER: 'Definitief nummer, nooit verzonden',
  IMPORT: 'Geïmporteerde factuur',
  FINQLE_PROFORMA: 'Interne Finqle-proforma',
  FINQLE_IMPORT: 'Finqle-import (UBL-retour)',
  FINQLE_SPIEGEL: 'Gespiegelde Finqle-factuur',
}

/** Waarom een factuur NIET verwijderbaar is. */
export const FACTUUR_VERWIJDER_BLOKKADES = [
  'AL_VERWIJDERD',
  'DEFINITIEF_VERZONDEN',
  'FINQLE_DEFINITIEF',
] as const
export type FactuurVerwijderBlokkade = (typeof FACTUUR_VERWIJDER_BLOKKADES)[number]

/**
 * Finqle-factuurstatussen waarbij de factuur bij Finqle definitief de deur uit
 * is (spec 143 par. 6.3: "API zegt INVOICED"). CREATED / SUBMITTED /
 * DISAPPROVED zijn nog geen definitief document richting de debiteur; alles
 * vanaf het akkoord-traject wel. Onbekend/leeg telt niet als definitief.
 */
export const FINQLE_INVOICE_DEFINITIEVE_STATUSSEN = [
  'PENDING_DEBTOR_APPROVAL',
  'PENDING_SIGN_OFF',
  'APPROVED',
  'ON_HOLD',
  'TO_BE_PAID',
  'OPEN',
  'FINALIZED',
  'CLOSED',
] as const

/** Bovengrens voor de vrije verwijder-reden (verantwoording, geen roman). */
export const FACTUUR_VERWIJDER_REDEN_MAX = 500

/** Vaste uitleg wanneer het recht `FACTUREN.canDelete` ontbreekt (knop zichtbaar-maar-uit). */
export const FACTUUR_VERWIJDEREN_GEEN_RECHT_UITLEG =
  'Verwijderen vereist het recht Facturen - verwijderen. Vraag een beheerder om dit recht toe te kennen via de rechtenmatrix.'

/**
 * Invoer van de pure resolver `bepaalFactuurVerwijderbaarheid` (@streep/utils):
 * alleen scalars van de factuur plus twee tellingen. Datums mogen ISO-strings
 * of Date-objecten zijn (server-DTO's serialiseren naar ISO).
 */
export interface FactuurVerwijderbaarheidInput {
  deletedAt: string | Date | null
  status: InvoiceStatus
  origin: InvoiceOrigin
  invoiceNumber: string | null
  isFinqleProforma: boolean
  isCredit: boolean
  isLocked: boolean
  portaalZichtbaar: boolean
  finqleApiInvoiceId: string | null
  finqleApiStatus: string | null
  finqleApiBetaaldDoorKlantOp: string | Date | null
  finqleApiBetaaldAanStreepOp: string | Date | null
  /** Finqle UBL-retour-import (`finqleXmlPath` of `finqleGeimporteerdOp` gevuld). */
  isFinqleImport: boolean
  betaaldOp: string | Date | null
  definitiefOp: string | Date | null
  pdfVerzondenOp: string | Date | null
  zenvoicesXmlVerzondenOp: string | Date | null
  verzondenNaarFinqleOp: string | Date | null
  verzondenNaarAdminOp: string | Date | null
  verzondenNaarKlantOp: string | Date | null
  exportedAt: string | Date | null
  /**
   * `Entity.invoiceBvCode` van de facturerende BV. Een eigen reeksnummer wordt
   * alleen een bewust FO-421-gat als zijn BV-code hiermee overeenkomt; anders
   * blijft het nummer als verbruikt op de verwijderde rij staan.
   */
  entityInvoiceBvCode: string | null
  werkbonCount: number
  billingRequestCount: number
}

/**
 * Uitkomst van de resolver; als `verwijderbaarheid` in de lijst- en detail-DTO
 * (de UI doet geen eigen statuscheck meer).
 */
export interface FactuurVerwijderbaarheidDTO {
  kan: boolean
  soort: FactuurVerwijderSoort | null
  blokkade: FactuurVerwijderBlokkade | null
  /** Reden verplicht bij origin ongelijk aan eigen concept (dubbel / andere klant / foutief aangeleverd). */
  redenVerplicht: boolean
  /** Eén zin: waarom (niet). */
  uitleg: string
  /** Wat er gebeurt bij verwijderen (bevestigingsdialoog). */
  gevolgen: string[]
  /** Aandachtspunten die verwijderen niet blokkeren. */
  waarschuwingen: string[]
  /** Via welke kanalen de factuur al de deur uit is (t.b.v. de blokkade-uitleg). */
  verzondenVia: string[]
}

/**
 * Knop-status voor de drie UI-plekken: zonder `FACTUREN.canDelete` zichtbaar-
 * maar-uitgeschakeld met uitleg (spec par. 3.6), anders de resolver-uitleg.
 */
export function factuurVerwijderKnopStatus(
  canDelete: boolean,
  verwijderbaarheid: FactuurVerwijderbaarheidDTO | null | undefined,
): { uitgeschakeld: boolean; uitleg: string } {
  if (!canDelete) return { uitgeschakeld: true, uitleg: FACTUUR_VERWIJDEREN_GEEN_RECHT_UITLEG }
  if (!verwijderbaarheid) {
    return { uitgeschakeld: true, uitleg: 'Verwijderbaarheid nog niet bepaald.' }
  }
  if (!verwijderbaarheid.kan) return { uitgeschakeld: true, uitleg: verwijderbaarheid.uitleg }
  return { uitgeschakeld: false, uitleg: verwijderbaarheid.uitleg }
}

// ─── Verwijder-record (lijst-filter "verwijderd") ────────────────────────────

export interface FactuurVerwijderingDTO {
  soort: FactuurVerwijderSoort
  soortLabel: string
  reden: string | null
  verwijderdOp: string
  verwijderdDoor: { id: string; naam: string } | null
  /** Het bij verwijderen vrijgegeven factuurnummer (de rij zelf heeft het niet meer). */
  invoiceNumber: string | null
  /** Aantal werkbonnen dat bij herstel weer gekoppeld wordt. */
  werkbonCount: number
}

// ─── Genegeerd-marker ────────────────────────────────────────────────────────

export const FACTUUR_NEGEER_SOORTEN = ['FINQLE_API_FACTUUR', 'FINQLE_IMPORT', 'FINQLE_PROFORMA'] as const
export type FactuurNegeerSoort = (typeof FACTUUR_NEGEER_SOORTEN)[number]

export const FACTUUR_NEGEER_SOORT_LABELS: Record<FactuurNegeerSoort, string> = {
  FINQLE_API_FACTUUR: 'Finqle-API-factuur',
  FINQLE_IMPORT: 'Finqle-import (factuurnummer)',
  FINQLE_PROFORMA: 'Interne proforma (werkbon)',
}

export interface FactuurNegeerMarkerDTO {
  id: string
  soort: FactuurNegeerSoort
  soortLabel: string
  sleutel: string
  invoiceId: string | null
  invoiceNummer: string | null
  clientNaam: string | null
  entityNaam: string | null
  reden: string
  gezetOp: string
  gezetDoor: { id: string; naam: string } | null
}

export interface FactuurNegeerMarkerListMeta {
  total: number
  page: number
  pageSize: number
}

// ─── Request-schema's (gedeeld: API `.safeParse` + form-validatie) ───────────

const redenSchema = z
  .string()
  .trim()
  .max(FACTUUR_VERWIJDER_REDEN_MAX, `Reden mag maximaal ${FACTUUR_VERWIJDER_REDEN_MAX} tekens zijn`)

/** Body van `DELETE /invoices/:id` (optioneel; reden verplicht per resolver). */
export const factuurVerwijderenBodySchema = z.object({ reden: redenSchema.optional() }).strict()
export type FactuurVerwijderenInput = z.infer<typeof factuurVerwijderenBodySchema>

/** Body van `POST /invoices/bulk-verwijderen`. */
export const factuurBulkVerwijderenSchema = z.object({
  ids: z.array(z.string().min(1)).min(1, 'Selecteer minimaal één factuur').max(200),
  reden: redenSchema.optional(),
})
export type FactuurBulkVerwijderenInput = z.infer<typeof factuurBulkVerwijderenSchema>

/** Body van `POST /invoices/negeer-markers/:id/opheffen` ("toch importeren"). */
export const factuurNegeerMarkerOpheffenSchema = z.object({ reden: redenSchema.optional() })

/** Query van `GET /invoices/negeer-markers`. */
export const factuurNegeerMarkersQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
})

// ─── Response-DTO's ──────────────────────────────────────────────────────────

export const FACTUUR_VERWIJDER_UITKOMSTEN = [
  'VERWIJDERD',
  'GEWEIGERD',
  'REDEN_VERPLICHT',
  'NIET_GEVONDEN',
  'FOUT',
] as const
export type FactuurVerwijderUitkomst = (typeof FACTUUR_VERWIJDER_UITKOMSTEN)[number]

export const FACTUUR_VERWIJDER_UITKOMST_LABELS: Record<FactuurVerwijderUitkomst, string> = {
  VERWIJDERD: 'Verwijderd',
  GEWEIGERD: 'Niet verwijderbaar',
  REDEN_VERPLICHT: 'Reden verplicht',
  NIET_GEVONDEN: 'Niet gevonden',
  FOUT: 'Fout',
}

/** Eén rij in het bulk-resultaat (ook de uitvallers, met reden). */
export interface FactuurVerwijderResultaatDTO {
  id: string
  invoiceNumber: string | null
  status: FactuurVerwijderUitkomst
  soort: FactuurVerwijderSoort | null
  reden: string | null
}

export interface FactuurBulkVerwijderResultaatDTO {
  resultaten: FactuurVerwijderResultaatDTO[]
  samenvatting: { verwijderd: number; geweigerd: number }
}

/** Antwoord van `DELETE /invoices/:id` (naast de 204-compatibele statuscode). */
export interface FactuurVerwijderDetailResultaatDTO {
  id: string
  invoiceNumber: string | null
  soort: FactuurVerwijderSoort
  werkbonnenOntkoppeld: number
  billingRequestsOntkoppeld: number
  markersGezet: number
  /** Het als bewust gat geregistreerde reeksnummer (FO-421), of null. */
  bewustGatNummer: string | null
  /**
   * batch-63 (FO-471 O6) — het reeksnummer blijft op de verwijderde factuur
   * staan (verbruikt, nooit opnieuw uitgegeven).
   */
  nummerBehouden?: boolean
  /**
   * batch-63 (FO-470) — het facturatieverzoek dat de gegevens van een oude
   * interne proforma overnam (bestaand of nieuw vastgelegd).
   */
  facturatieverzoekIds?: string[]
}

export interface FactuurHerstelResultaatDTO {
  id: string
  invoiceNumber: string | null
  werkbonnenGekoppeld: number
  billingRequestsGekoppeld: number
  markersOpgeheven: number
}
